<template>
  <div class="table-container">
    <!-- Search Input -->
    <input
      v-model="searchQuery"
      placeholder="Search..."
      class="search-input"
    />

    <!-- Table -->
    <table class="custom-table">
      <thead>
        <tr>
          <th
            v-for="column in displayedColumns"
            :key="column"
            @click="sortBy(column)"
            class="sortable"
          >
            <div class="header-content">
              {{ headerLabels[column] || formatHeader(column) }}
              <span class="sort-icon">
                <template v-if="sortColumn === column">
                  {{ sortOrder === 'asc' ? '🔼' : '🔽' }}
                </template>
                <template v-else>↕️</template>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in paginatedData" :key="rowIndex">
          <td v-for="column in displayedColumns" :key="column">
            <slot :name="`cell-${column}`" :row="row">
              {{ row[column] }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div class="pagination-controls">
      <button @click="prevPage" :disabled="currentPage === 1">
        Previous
      </button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="nextPage" :disabled="currentPage === totalPages">
        Next
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Table',
  props: {
    data: {
      type: Array,
      required: true,
    },
    columnsToShow: {
      type: Array,
      default: () => [],
    },
    headerLabels: {
      type: Object,
      default: () => ({})
    },
    pageSize: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      currentPage: 1,
      sortColumn: '',
      sortOrder: 'asc',
      searchQuery: '',
    };
  },
  computed: {
    displayedColumns() {
      if (this.columnsToShow.length > 0) {
        return this.columnsToShow;
      } else if (this.data.length > 0) {
        return Object.keys(this.data[0]);
      }
      return [];
    },
    filteredData() {
      if (!this.searchQuery) {
        return this.data;
      }
      return this.data.filter((row) =>
        this.displayedColumns.some((column) =>
          String(row[column])
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        )
      );
    },
    sortedData() {
      if (!this.sortColumn) {
        return this.filteredData;
      }
      return [...this.filteredData].sort((a, b) => {
        const aValue = a[this.sortColumn];
        const bValue = b[this.sortColumn];
        if (aValue < bValue) return this.sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedData.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.sortedData.length / this.pageSize);
    },
  },
  methods: {
    formatHeader(header) {
      return header
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortOrder = 'asc';
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
  },
};
</script>

<style scoped>
.table-container {
  margin: 20px;
  font-family: Arial, sans-serif;
}

.search-input {
  margin-bottom: 10px;
  padding: 8px;
  width: 50%;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.custom-table th,
.custom-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
  word-wrap: break-word;
}

.custom-table th {
  background-color: #f4f4f4;
  cursor: pointer;
  user-select: none;
}

.custom-table th:hover {
  background-color: #e0e0e0;
}

.custom-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.custom-table tr:hover {
  background-color: #f1f1f1;
}

.sortable .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
}

.sort-icon {
  margin-left: 8px;
  font-size: 1em;
}

.pagination-controls {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-controls button {
  padding: 8px 12px;
  margin: 0 5px;
  border: 1px solid #ccc;
  background-color: #fff;
  cursor: pointer;
  border-radius: 4px;
}

.pagination-controls button:disabled {
  background-color: #f4f4f4;
  cursor: not-allowed;
}

.pagination-controls span {
  margin: 0 10px;
}
</style>
